import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import { LIMITS, type Note, type Role } from '@chatapp-inbox/shared';
import { Button } from './Button';
import { CharacterCounter, showsCounter } from './CharacterCounter';
import { formatFullDate, formatNoteTime } from './note-time';
import { noteErrorMessage, useDeleteNote, useUpdateNote } from './use-notes';
import styles from './NoteItem.module.css';

type Mode = 'view' | 'edit' | 'confirmDelete';

interface NoteItemProps {
  note: Note;
  conversationId: string;
  viewer: { id: string; role: Role };
  now: Date;
  /** Resultado de uma ação, para a região de avisos da seção ("Nota salva."); texto vazio limpa o aviso. */
  onNotice: (text: string) => void;
  /** A nota foi apagada: a seção leva o foco para um lugar que continue existindo. */
  onDeleted: (noteId: string) => void;
}

/**
 * Uma nota interna. O AUTOR edita e apaga; o ADMINISTRADOR também apaga as dos outros (não edita:
 * o servidor recusa). O texto é sempre texto puro, com as quebras de linha preservadas.
 *
 * O foco acompanha cada troca de modo (editar, confirmar, voltar): o botão clicado costuma sumir, e
 * o foco nunca pode cair no <body>.
 */
export function NoteItem({
  note,
  conversationId,
  viewer,
  now,
  onNotice,
  onDeleted,
}: NoteItemProps): ReactElement {
  const uid = useId();
  const [mode, setMode] = useState<Mode>('view');
  const [text, setText] = useState(note.text);
  const update = useUpdateNote(conversationId, note.id);
  const remove = useDeleteNote(conversationId, note.id);

  const isAuthor = note.authorUserId === viewer.id;
  const canDelete = isAuthor || viewer.role === 'admin';

  const editRef = useRef<HTMLButtonElement>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const cancelDeleteRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousMode = useRef<Mode>('view');

  useEffect(() => {
    const before = previousMode.current;
    previousMode.current = mode;
    if (before === mode) return;
    if (mode === 'edit') {
      const field = textareaRef.current;
      field?.focus();
      field?.setSelectionRange(field.value.length, field.value.length); // cursor no fim do texto
    } else if (mode === 'confirmDelete') {
      cancelDeleteRef.current?.focus(); // a resposta segura, não a destrutiva
    } else if (before === 'edit') {
      editRef.current?.focus();
    } else {
      deleteRef.current?.focus();
    }
  }, [mode]);

  const trimmed = text.trim();
  const over = trimmed.length > LIMITS.noteText;
  const unchanged = trimmed === note.text.trim();
  const canSave = trimmed.length > 0 && !over && !unchanged;
  const saving = update.isPending;
  const deleting = remove.isPending;

  const startEditing = (): void => {
    // O aviso da ação anterior ("Nota apagada.") era sobre outra coisa: não fica ao lado do campo.
    onNotice('');
    update.reset();
    remove.reset();
    setText(note.text);
    setMode('edit');
  };

  const cancelEditing = (): void => {
    update.reset();
    setText(note.text);
    setMode('view');
  };

  const save = (): void => {
    if (!canSave || saving) return;
    onNotice(''); // o aviso da ação anterior não fica ao lado do resultado desta
    update.mutate(trimmed, {
      onSuccess: () => {
        setMode('view');
        onNotice('Nota salva.');
      },
    });
  };

  const confirmDelete = (): void => {
    if (deleting) return;
    onNotice('');
    remove.mutate(undefined, {
      onSuccess: () => {
        onNotice('Nota apagada.');
        onDeleted(note.id);
      },
      // A confirmação some junto com o erro; o aviso aparece sob a nota e o foco volta a "Apagar".
      onError: () => setMode('view'),
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (
      event.key === 'Enter' &&
      (event.ctrlKey || event.metaKey) &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (!saving) cancelEditing();
    }
  };

  const metaId = `${uid}-meta`;
  const fieldId = `${uid}-field`;
  const counterId = `${uid}-counter`;
  const promptId = `${uid}-prompt`;
  const errorText = update.isError
    ? noteErrorMessage(update.error, 'Não foi possível salvar a nota. Tente de novo.')
    : remove.isError
      ? noteErrorMessage(remove.error, 'Não foi possível apagar a nota. Tente de novo.')
      : null;

  return (
    <article
      className={styles.note}
      // A seção devolve o foco a esta nota quando a vizinha é apagada (ver `data-note-id`).
      data-note-id={note.id}
      tabIndex={-1}
      aria-labelledby={metaId}
    >
      <div id={metaId} className={styles.meta}>
        <span dir="auto" className={styles.author}>
          {note.authorName}
        </span>
        <time
          className={styles.time}
          dateTime={note.createdAt}
          title={formatFullDate(note.createdAt)}
        >
          {formatNoteTime(note.createdAt, now)}
        </time>
        {note.updatedAt && (
          <span className={styles.edited} title={`Editada em ${formatFullDate(note.updatedAt)}`}>
            editada
          </span>
        )}
      </div>

      {mode === 'edit' ? (
        <div className={styles.editor}>
          <label htmlFor={fieldId} className={styles.label}>
            Editar nota
          </label>
          <textarea
            id={fieldId}
            ref={textareaRef}
            dir="auto"
            rows={4}
            className={styles.textarea}
            value={text}
            readOnly={saving}
            aria-invalid={over || undefined}
            aria-describedby={showsCounter(trimmed.length, LIMITS.noteText) ? counterId : undefined}
            onKeyDown={onKeyDown}
            onChange={(event) => {
              setText(event.target.value);
              if (update.isError) update.reset();
            }}
          />
          <CharacterCounter id={counterId} length={trimmed.length} limit={LIMITS.noteText} />
          <div className={styles.actions}>
            <Button variant="primary" busy={saving} disabled={!canSave} onClick={save}>
              Salvar
            </Button>
            <Button busy={saving} onClick={cancelEditing}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <p dir="auto" className={styles.text}>
          {note.text}
        </p>
      )}

      {mode === 'confirmDelete' && (
        <div role="group" aria-labelledby={promptId} className={styles.confirm}>
          <p id={promptId} className={styles.confirmText}>
            Apagar esta nota?
          </p>
          <div className={styles.actions}>
            <Button variant="danger" busy={deleting} onClick={confirmDelete}>
              Sim, apagar
            </Button>
            <Button
              ref={cancelDeleteRef}
              busy={deleting}
              onClick={() => {
                remove.reset();
                setMode('view');
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {mode === 'view' && canDelete && (
        <div className={styles.actions}>
          {isAuthor && (
            <Button ref={editRef} aria-describedby={metaId} onClick={startEditing}>
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              ref={deleteRef}
              variant="dangerOutline"
              aria-describedby={metaId}
              onClick={() => {
                onNotice(''); // "Nota adicionada." não fica ao lado da pergunta "Apagar esta nota?"
                update.reset();
                remove.reset();
                setMode('confirmDelete');
              }}
            >
              Apagar
            </Button>
          )}
        </div>
      )}

      {errorText && (
        <p role="alert" className={styles.error}>
          {errorText}
        </p>
      )}
    </article>
  );
}
